import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, EmptyState, Input, Wordmark } from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import {
  InviteNotRedeemableError,
  acceptInvite,
  previewInvite,
  type InvitePreview,
} from "../../api/invite-redemption-api.js";

/**
 * PB-4922 (AC-15). Mounted on `/invite/:token`, OUTSIDE the auth guard.
 *
 * The visitor has no session and no account — that is the whole point — so this
 * renders standalone, exactly as `CallbackPage` does: no `AppLayout`, no top
 * bar, no tab strip. An anonymous visitor has no tabs, no profile and no
 * notifications, and mounting the chrome would mean teaching every piece of it
 * to tolerate a null session.
 *
 * NOTHING TERMINAL NAVIGATES. Every failure state stays on this route and offers
 * no automatic retry, which is the same rule `CallbackPage` documents and for
 * the same recorded reason: navigating away mounts `RequireAuth`, which begins a
 * login the invitee cannot complete, turning one failure into a redirect loop
 * and eventually a Keycloak 429 that locks them out of the account they were
 * being given.
 *
 * SUCCESS IS THE ONE CONTROLLED CONTINUATION: the accept response returns a
 * same-origin `/set-password#token` URL and the page replaces this history
 * entry with it. The fragment never reaches the server or proxy request logs,
 * while an invitee can choose a credential immediately instead of being sent
 * to a sign-in screen for a password that does not exist yet. The separately
 * emailed set-password link remains the recovery route when this browser is
 * closed before that continuation completes.
 *
 * The token is read from the path because an emailed link has nowhere else to
 * carry it, but it is sent in the request BODY — see `invite-redemption-api.ts`.
 * It is never written to storage: `localStorage` and `sessionStorage` are
 * forbidden for security-relevant state, and this is the only credential the
 * invitee holds.
 */
export function InvitePage(): ReactElement {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  /* `"dead"` means the server refused the token — terminal, nothing the invitee
     can do. `"unreachable"` means the request never got an answer it could
     trust: a 503 while the service restarts, a 429, a dropped connection. The
     page used to collapse both into the terminal screen, which inverted the
     server's deliberate 404-vs-503 distinction in the direction that harms the
     invitee — telling somebody holding a perfectly good link to go and ask for
     a new one. */
  const [loadFailure, setLoadFailure] = useState<null | "dead" | "unreachable">(
    null,
  );
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [refused, setRefused] = useState(false);
  /* Guards React 18 StrictMode's double mount, the same way CallbackPage does.
     The preview is read-only so a second fetch would be harmless, but a second
     one is still a second unauthenticated request from a page whose whole job is
     to make as few as possible. */
  const previewInFlight = useRef(false);

  const loadPreview = useCallback((): void => {
    if (!token) return;
    setLoadFailure(null);
    previewInvite(token)
      .then(setPreview)
      .catch((error: unknown) => {
        /* Still ONE state for every REFUSAL — an expired token and an unknown
           one must stay indistinguishable, or the page rebuilds the oracle the
           server was careful not to give. What is separated here is a refusal
           from a non-answer: `InviteNotRedeemableError` is the server's single
           404, and anything else means it did not get to decide. */
        setLoadFailure(
          error instanceof InviteNotRedeemableError ? "dead" : "unreachable",
        );
      });
  }, [token]);

  useEffect(() => {
    if (previewInFlight.current) return;
    previewInFlight.current = true;
    loadPreview();
  }, [loadPreview]);

  const submit = async (): Promise<void> => {
    if (!token || submitting) return;
    setSubmitting(true);
    setRefused(false);
    try {
      const { setPasswordUrl } = await acceptInvite(token, email);
      const continuation = new URL(setPasswordUrl, window.location.origin);
      if (
        continuation.origin !== window.location.origin ||
        continuation.pathname !== "/set-password" ||
        continuation.hash.length <= 1
      ) {
        throw new Error("The password setup continuation is invalid.");
      }
      /* Keep the one-time token in the fragment. React Router changes only the
         browser location, so neither the BFF nor an ingress request sees it. */
      navigate(`${continuation.pathname}${continuation.hash}`, {
        replace: true,
      });
    } catch (_error) {
      /* A refusal and an unreachable service are shown the same way, because the
         invitee can do nothing different about either — and the difference is
         exactly what the server declines to disclose. */
      setRefused(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadFailure === "unreachable") {
    /* A retry button here, where the terminal screen deliberately has none. The
       rule that screen states — "an affordance that cannot work is worse than
       none" — is exactly why this branch exists separately: here the affordance
       CAN work, because the link may be perfectly good and the service merely
       busy. Retrying is also the only thing the invitee can do; the alternative
       is asking for a replacement link that was never needed. */
    /* The page-level status screen: EmptyState lg, error (OK-32). */
    return (
      <Standalone role="alert">
        <EmptyState
          variant="error"
          size="lg"
          title="We couldn't check your invitation."
          description="The service didn't answer. Your link may still be fine — try again in a moment."
          action={
            <Button variant="secondary" onClick={loadPreview}>
              Try again
            </Button>
          }
        />
      </Standalone>
    );
  }

  if (!token || loadFailure === "dead") {
    /* `role="alert"` and no retry affordance at all, following
       `CollaborationConfigurationUnavailable` in App.tsx rather than
       CallbackPage's error state: that one offers a retry button because a
       fresh sign-in can genuinely fix it, and here nothing the invitee does
       can. An affordance that cannot work is worse than none. */
    /* The page-level status screen: EmptyState lg, error (OK-32). */
    return (
      <Standalone role="alert">
        <EmptyState
          variant="error"
          size="lg"
          title="This invitation link is not valid."
          description="It may have already been used, or it may have expired. Ask whoever invited you to send a new one."
        />
      </Standalone>
    );
  }

  if (!preview) {
    /* Kept as plain text: a Spinner ring beside it is a look change no PR 3
       row approves (OK-38 names other files). */
    return (
      <Standalone>
        <p>Checking your invitation…</p>
      </Standalone>
    );
  }

  return (
    <Standalone>
      <p className="text-lg font-medium text-pb-text">
        Join {preview.workspaceName}
      </p>
      {/* The address the invitee re-types is a TYPO GUARD, not an
          authentication factor, and the copy says so rather than implying a
          check it does not perform: whoever holds this link can read the address
          in the message they are holding. What it genuinely catches is the wrong
          person in a shared mailbox, and an address the administrator mistyped. */}
      <p className="max-w-md text-sm">
        Confirm the email address this invitation was sent to.
      </p>
      {/* A REAL form, so Enter submits. `packages/ui`'s Button defaults to
          `type="button"`, so a bare click handler leaves the keyboard with no
          way to submit at all — and this is a single-field form on the first
          page a new customer user ever sees, where typing an address and
          pressing Enter is the obvious thing to do. */}
      <form
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* The width cap lives HERE rather than on the Input: `packages/ui`'s
            Input hardcodes `w-full` on its own wrapper and passes `className`
            through to the inner field, so the page is the only place that can
            constrain it. Without this the field stretched the full viewport —
            1232px against a 341px paragraph at desktop width — while every other
            element on the page is capped at `max-w-md`. Unit tests cannot see it;
            a browser at 1280px makes it obvious, and at 375px it changes nothing. */}
        <div className="w-full max-w-md">
          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={submitting || email.trim().length === 0}
        >
          {submitting ? "Setting up…" : "Continue"}
        </Button>
      </form>
      {/* Kept as plain text: Alert (inline included) adds a glyph and its
          own type and colour, a look change no PR 3 row approves. */}
      {refused ? (
        <p className="max-w-md text-sm" role="alert">
          That did not work. Check the address and try again, or ask whoever
          invited you to send a new link.
        </p>
      ) : null}
    </Standalone>
  );
}

/** The standalone frame, matching `CallbackPage`'s — no shell chrome at all. */
function Standalone({
  children,
  role,
}: {
  readonly children: React.ReactNode;
  readonly role?: string;
}): ReactElement {
  return (
    <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
      {/* The product mark, at the top of the column on every state. It sits
          OUTSIDE the role below, so an alert announces its message and not
          the product name first. */}
      <div className="mb-3 text-pb-text">
        <Wordmark label={PRODUCT_NAME} />
      </div>
      <div
        className="flex w-full flex-col items-center gap-3"
        {...(role ? { role } : {})}
      >
        {children}
      </div>
    </div>
  );
}
